/**
 * La foto de perfil de NUESTRO lado del chat — la que va en los mensajes salientes.
 *
 * 🪤 ES POR LÍNEA, NO POR TENANT, Y ESA DISTINCIÓN YA COSTÓ UN BUG. Cuando se leía la línea por
 * defecto del tenant, todos los mensajes salientes mostraban la misma foto: la de Shalom pegada en
 * los chats de otras líneas. Un negocio con dos números son dos identidades distintas para el
 * cliente, y el avatar es lo que las separa a simple vista.
 *
 * Vive en un hook porque el chat de WhatsApp ya no se monta en un solo lugar: la bandeja de Chats y
 * el chat rápido de Operaciones necesitan la misma foto, y resolverla mal en uno de los dos es
 * exactamente el bug de arriba otra vez.
 *
 * 🪤 VA POR `useQuery`, NO POR `useEffect`. La primera versión pedía la foto en un efecto y el
 * trinquete de carga de datos la rechazó (ADR-0012). Tenía razón, y acá se nota más que en otros
 * lados: con tres ventanas de chat acopladas abiertas sobre la misma línea, un efecto por montaje
 * son tres consultas idénticas; con caché es una sola, y al minimizar y reabrir no se vuelve a
 * pedir. La clave incluye la línea porque la línea ES la identidad de la consulta.
 *
 * Sin `phoneLineId` cae a la línea por defecto del tenant — es lo correcto cuando todavía no hay
 * conversación abierta, y es lo que hace un negocio de una sola línea, que es la mayoría.
 */

import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';

async function traerAvatar(tenantId: string, phoneLineId: string | null): Promise<string | null> {
  const consulta = phoneLineId
    ? supabase.from('wa_phone_lines').select('profile_pic_url').eq('id', phoneLineId).maybeSingle()
    : supabase.from('wa_phone_lines')
      .select('profile_pic_url')
      .eq('tenant_id', tenantId)
      .eq('is_active', true)
      .order('is_default', { ascending: false })
      .order('created_at', { ascending: true })
      .limit(1)
      .maybeSingle();

  const { data } = await consulta;
  return (data as { profile_pic_url?: string | null } | null)?.profile_pic_url ?? null;
}

export function useWaLineAvatar(tenantId: string | null, phoneLineId: string | null): string | null {
  const { data } = useQuery({
    queryKey: ['waLineAvatar', tenantId, phoneLineId],
    queryFn: () => traerAvatar(tenantId!, phoneLineId),
    enabled: !!tenantId,
    // La foto de una línea cambia cuando el dueño la cambia en WhatsApp: casi nunca. Media hora de
    // frescura evita pedirla en cada apertura de chat sin que quede desactualizada de forma visible.
    staleTime: 30 * 60 * 1000,
  });

  return data ?? null;
}
